import { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { Search, X, FileText, Loader2 } from "lucide-react";
import { Modal }             from "@/components/ui/Modal";
import { MultiSelect }       from "@/components/ui/MultiSelect";
import {
  OutcomeItem,
  BankStatementMovement,
  BankMovementFinanceiroLine,
  outcomeParcelValorExibido,
  fetchBillsNotes,
  fetchBillInstallmentTotalsByIds,
} from "@/services/financeiro";
import { fetchPurchaseOrderSummary } from "@/services/suprimentos";
import { formatCurrency, formatDate } from "@/utils/format";
import { cn } from "@/utils/cn";
import { useAuth } from "@/hooks/useAuth";
import { StatementTable } from "./FinanceiroModalContent";
import { noteTextForItem, seedNotesMapFromItems, extractPurchaseOrderRefFromNotes } from "../noteHelpers";
import { generatePurchaseOrderPdf } from "../pdf/purchaseOrderPdfActions";
import { readInstallmentTotalsCacheForBillIds } from "@/lib/installmentTotalsCache";

/* ─── Props ──────────────────────────────────────────────────────────────── */

export interface DespesasDetailModalProps {
  open:             boolean;
  onClose:          () => void;
  title:            string;
  items:            OutcomeItem[];
  /** Débitos do extrato bancário SEMOG somados ao KPI de despesas */
  statementDebits?: BankStatementMovement[];
  /** Linhas bulk operação Pagamento (mesmo total do KPI; pode existir mesmo sem linhas SEMOG). */
  bankMovementPagamentos?: BankMovementFinanceiroLine[];
  /** billId → total de parcelas; null enquanto carrega */
  installmentsMap:  Map<string, number> | null;
  /** costCenterId → label; para exibição amigável */
  ccNameMap:        Map<string, string>;
  /** paymentCategoryId → nome do plano financeiro */
  paymentCatMap:    Map<string, string>;
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

const PER_PAGE = 25;

function billIdKey(billId: string): string {
  return String(billId ?? "").trim();
}

function parcelaStr(
  item: OutcomeItem,
  map: Map<string, number> | null,
  fallbackTotalsByBill: Map<string, number>
): string {
  const num = item.installmentNumber || 1;
  const id = billIdKey(item.billId);
  const fromMap = map && id ? (map.get(id) ?? null) : null;
  const fromItems = id ? (fallbackTotalsByBill.get(id) ?? null) : null;
  const total = fromMap ?? item.installmentsTotal ?? fromItems ?? num;
  return `${num}/${Math.max(num, total)}`;
}

/** Igual ao critério de `need` no fetch Sienge — total ainda não confiável (ex. 9/9 em vez de 9/40). */
function billParcelaUnresolved(
  billId: string,
  mergedMap: Map<string, number> | null,
  maxByBill: Map<string, number>
): boolean {
  const id = billIdKey(billId);
  if (!id) return false;
  const g = mergedMap?.get(id) ?? null;
  const maxSeen = maxByBill.get(id) ?? 0;
  if (g == null || !Number.isFinite(g) || g <= 0) return true;
  return maxSeen > 1 && g === maxSeen;
}

function ccLabel(item: OutcomeItem, ccNameMap: Map<string, string>): string {
  return item.costCenterName
    || (item.costCenterId ? ccNameMap.get(item.costCenterId) ?? item.costCenterId : "")
    || "—";
}

function accountLabel(item: OutcomeItem, paymentCatMap: Map<string, string>): string {
  /* 1. Nome resolvido via API de payment-categories (mais completo) */
  if (item.accountId) {
    const fromMap = paymentCatMap.get(item.accountId);
    if (fromMap) return fromMap;
  }
  /* 2. Nome embutido no bulk-data (quando disponível) */
  if (item.accountName) return item.accountName;
  /* 3. Fallback: exibe o ID */
  if (item.accountId) return `Plano ${item.accountId}`;
  return "—";
}

function matchFilter(value: string, filter: string): boolean {
  if (!filter) return true;
  return value.toLowerCase().includes(filter.toLowerCase());
}

function labelsFilterMatch(selected: string[], cellValue: string): boolean {
  if (selected.length === 0) return true;
  const v = cellValue.trim().toLowerCase();
  if (!v || v === "—") return false;
  return selected.some((s) => s.trim().toLowerCase() === v);
}

/* ─── Filtros ────────────────────────────────────────────────────────────── */

interface Filters {
  titulo:    string;
  parcela:   string;
  vencimento: string;
  pagamento: string;
  credor:    string;
  valor:     string;
  /** Rótulos de centro de custo selecionados (multiselect). */
  centros:   string[];
  /** Rótulos de plano financeiro selecionados (multiselect). */
  planos:    string[];
  notas:     string;
  status:    string;
}

const EMPTY_FILTERS: Filters = {
  titulo: "", parcela: "", vencimento: "", pagamento: "",
  credor: "", valor: "", centros: [], planos: [], notas: "", status: "",
};

function hasActiveFilters(f: Filters): boolean {
  if (f.planos.length > 0 || f.centros.length > 0) return true;
  return (
    f.titulo.trim() !== "" ||
    f.parcela.trim() !== "" ||
    f.vencimento.trim() !== "" ||
    f.pagamento.trim() !== "" ||
    f.credor.trim() !== "" ||
    f.valor.trim() !== "" ||
    f.notas.trim() !== "" ||
    f.status.trim() !== ""
  );
}

/** Linha da grade: parcela Sienge ou lançamento bulk movimento bancário (BC). */
type GridRow =
  | { kind: "outcome"; item: OutcomeItem }
  | { kind: "bankMove"; line: BankMovementFinanceiroLine };

function bankLineMatchesFilters(line: BankMovementFinanceiroLine, filters: Filters): boolean {
  const payBr = line.movementDate ? formatDate(line.movementDate) : "";
  const cred = `${line.historico} ${line.operationName}`.trim();
  const valorStr = formatCurrency(line.amount);
  const ccStr = "Mov. bancário (BC)";
  const accStr = line.operationName || "—";
  const notesStr = line.historico || "—";
  const statusStr = "pago";
  const tituloHay = `${line.id} ${line.historico}`.trim();

  return (
    (!filters.titulo.trim() || matchFilter(tituloHay, filters.titulo)) &&
    (!filters.parcela.trim() || matchFilter("—", filters.parcela)) &&
    (!filters.vencimento.trim() ||
      matchFilter(line.movementDate, filters.vencimento) ||
      matchFilter(payBr, filters.vencimento)) &&
    (!filters.pagamento.trim() ||
      matchFilter(line.movementDate, filters.pagamento) ||
      matchFilter(payBr, filters.pagamento)) &&
    (!filters.credor.trim() || matchFilter(cred, filters.credor)) &&
    (!filters.valor.trim() || matchFilter(valorStr, filters.valor)) &&
    labelsFilterMatch(filters.centros, ccStr) &&
    labelsFilterMatch(filters.planos, accStr) &&
    (!filters.notas.trim() || matchFilter(notesStr, filters.notas)) &&
    (!filters.status.trim() || matchFilter(statusStr, filters.status))
  );
}

function gridRowSortDate(r: GridRow): string {
  if (r.kind === "outcome") {
    const d = r.item.paymentDate || r.item.dueDate || "";
    return d.slice(0, 10);
  }
  return (r.line.movementDate || "").slice(0, 10);
}

/* ─── FilterInput ────────────────────────────────────────────────────────── */

function FilterInput({
  placeholder, value, onChange,
}: {
  placeholder: string;
  value:       string;
  onChange:    (v: string) => void;
}) {
  return (
    <div className="relative">
      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-300 pointer-events-none" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-6 pr-2 py-1 text-xs border border-slate-200 rounded-md bg-white text-slate-700 placeholder:text-slate-300 focus:outline-none focus:ring-1 focus:ring-brand-400 focus:border-brand-400"
      />
    </div>
  );
}

/* ─── StatusBadge ────────────────────────────────────────────────────────── */

function StatusBadge({ paid }: { paid: boolean }) {
  return (
    <span className={cn(
      "inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap",
      paid ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
    )}>
      {paid ? "Pago" : "Em Aberto"}
    </span>
  );
}

/* ─── Subtotal Footer ────────────────────────────────────────────────────── */

function SubtotalBar({
  filtered, total, totalValue, filteredValue,
}: {
  filtered:      number;
  total:         number;
  totalValue:    number;
  filteredValue: number;
}) {
  const changed = filtered !== total;
  return (
    <div className={cn(
      "flex flex-wrap items-center justify-between gap-x-6 gap-y-1 px-4 py-3 rounded-xl text-sm border",
      changed
        ? "bg-brand-50 border-brand-200 text-brand-900"
        : "bg-slate-50 border-slate-200 text-slate-700"
    )}>
      <span className="font-medium text-xs">
        {changed
          ? `${filtered} de ${total} registro${total !== 1 ? "s" : ""} (filtrados)`
          : `${total} registro${total !== 1 ? "s" : ""}`
        }
      </span>
      <div className="flex items-center gap-1">
        <span className="text-xs font-medium opacity-70">Subtotal:</span>
        <span className="font-bold text-base">
          {formatCurrency(filteredValue)}
        </span>
        {changed && (
          <span className="text-xs opacity-60 ml-1">
            / {formatCurrency(totalValue)} total
          </span>
        )}
      </div>
    </div>
  );
}

/* ─── Modal principal ────────────────────────────────────────────────────── */

export function DespesasDetailModal({
  open,
  onClose,
  title,
  items,
  statementDebits,
  bankMovementPagamentos,
  installmentsMap,
  ccNameMap,
  paymentCatMap,
}: DespesasDetailModalProps) {
  const { user } = useAuth();
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [page, setPage]       = useState(0);
  const [pdfLoadingKey, setPdfLoadingKey] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);

  /*
   * ── Notas: exibe imediatamente o que veio no bulk (item.notes); em paralelo
   * enriquece com GET /api/bills-notes e mescla sem bloquear a coluna.
   */
  const [notesMap, setNotesMap] = useState<Map<string, string>>(() => new Map());
  const [notesSyncPending, setNotesSyncPending] = useState(false);
  /** Totais por GET /bills/{id} ao abrir o modal — corrige mapa agregado incompleto ou 9/9 vs 9/40. */
  const [detailInstallmentTotals, setDetailInstallmentTotals] = useState<Map<string, number>>(
    () => new Map()
  );

  /** Observações já embutidas nas parcelas — disponível no 1.º render, sem esperar API. */
  const bulkNotesByBillId = useMemo(() => seedNotesMapFromItems(items), [items]);
  const maxInstallmentByBill = useMemo(() => {
    const m = new Map<string, number>();
    for (const it of items) {
      const id = billIdKey(it.billId);
      if (!id) continue;
      const n = Number(it.installmentNumber || 0);
      m.set(id, Math.max(m.get(id) ?? 0, n));
    }
    return m;
  }, [items]);
  const billIdsInItems = useMemo(
    () => new Set(items.map((i) => billIdKey(i.billId)).filter(Boolean)),
    [items]
  );
  const mergedInstallmentsMap = useMemo(() => {
    if (!installmentsMap && detailInstallmentTotals.size === 0) return null;
    const m = new Map<string, number>();
    if (installmentsMap) {
      for (const [k, v] of installmentsMap) {
        const key = billIdKey(k);
        if (key && Number.isFinite(v) && v > 0) m.set(key, v);
      }
    }
    for (const [k, v] of detailInstallmentTotals) {
      if (!billIdsInItems.has(k)) continue;
      if (Number.isFinite(v) && v > 0) m.set(k, v);
    }
    return m;
  }, [installmentsMap, detailInstallmentTotals, billIdsInItems]);
  const fallbackTotalsByBill = useMemo(() => {
    const m = new Map<string, number>();
    for (const it of items) {
      const billId = billIdKey(it.billId);
      if (!billId) continue;
      const curr = m.get(billId) ?? 0;
      const fromInstallment = Number(it.installmentNumber || 0);
      const fromItemTotal = Number(it.installmentsTotal || 0);
      const next = Math.max(curr, fromInstallment, fromItemTotal);
      if (next > 0) m.set(billId, next);
    }
    return m;
  }, [items]);

  const set = useCallback((key: keyof Filters) => (v: string) => {
    setFilters((prev) => ({ ...prev, [key]: v }));
    setPage(0);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setPage(0);
  }, []);

  /* Fecha e limpa */
  const handleClose = useCallback(() => {
    onClose();
    setFilters(EMPTY_FILTERS);
    setNotesMap(new Map());
    setNotesSyncPending(false);
    setDetailInstallmentTotals(new Map());
    setPdfLoadingKey(null);
    setPdfError(null);
    setPage(0);
  }, [onClose]);

  const handleGeneratePurchaseOrderPdf = useCallback(
    async (item: OutcomeItem, notesText: string) => {
      const poRef = extractPurchaseOrderRefFromNotes(notesText);
      if (!poRef) {
        setPdfError("Não foi possível identificar o pedido de compra nas notas.");
        return;
      }
      const rowKey = `${item.billId}-${item.installmentNumber}-${poRef}`;
      setPdfLoadingKey(rowKey);
      setPdfError(null);
      try {
        const summary = await fetchPurchaseOrderSummary(poRef);
        await generatePurchaseOrderPdf(summary, {
          geradoPor: user?.displayName?.trim() || user?.username?.trim() || "Usuário",
          billId: item.billId || undefined,
          creditorFallback: item.creditorName || undefined,
        });
      } catch (e) {
        setPdfError(e instanceof Error ? e.message : "Erro ao gerar PDF do pedido.");
      } finally {
        setPdfLoadingKey(null);
      }
    },
    [user?.displayName, user?.username],
  );

  const parcelaFetchGenRef = useRef(0);

  /* Totais Sienge: cache localStorage + POST só para títulos ainda “em dúvida”. */
  useEffect(() => {
    if (!open || items.length === 0) return;
    const ids = [...new Set(items.map((i) => billIdKey(i.billId)).filter(Boolean))];
    if (ids.length === 0) return;
    const cacheMap = readInstallmentTotalsCacheForBillIds(ids);
    const need = ids.filter((id) => {
      const fromCache = cacheMap.get(id);
      const g =
        fromCache != null && fromCache > 0 ? fromCache : (installmentsMap?.get(id) ?? null);
      const maxSeen = maxInstallmentByBill.get(id) ?? 0;
      if (g == null || !Number.isFinite(g) || g <= 0) return true;
      return maxSeen > 1 && g === maxSeen;
    });
    const slice = need.slice(0, 200);
    if (slice.length === 0) return;
    const gen = ++parcelaFetchGenRef.current;
    let cancelled = false;
    fetchBillInstallmentTotalsByIds(slice)
      .then((m) => {
        if (cancelled || parcelaFetchGenRef.current !== gen) return;
        if (m.size === 0) return;
        setDetailInstallmentTotals((prev) => {
          const next = new Map(prev);
          for (const [k, v] of m) next.set(k, v);
          return next;
        });
      })
      .catch(() => {
        /* mantém spinner até merged resolver; falha silenciosa como antes */
      });
    return () => {
      cancelled = true;
    };
  }, [open, items, installmentsMap, maxInstallmentByBill]);

  /* Notas + parcelas: antes do paint — bulk nas notas; totais de parcela vindos do cache local. */
  useLayoutEffect(() => {
    if (!open) return;
    if (items.length === 0) {
      setNotesMap(new Map());
      setDetailInstallmentTotals(new Map());
      return;
    }
    setNotesMap(seedNotesMapFromItems(items));
    const ids = [...new Set(items.map((i) => billIdKey(i.billId)).filter(Boolean))];
    setDetailInstallmentTotals(readInstallmentTotalsCacheForBillIds(ids));
  }, [open, items]);

  /* Enriquecimento Sienge em segundo plano — não zera o mapa (evita coluna “—” até a API). */
  useEffect(() => {
    if (!open || items.length === 0) return;
    const allIds = [...new Set(items.map((i) => billIdKey(i.billId)).filter(Boolean))];
    if (allIds.length === 0) return;
    let cancelled = false;
    setNotesSyncPending(true);
    fetchBillsNotes(allIds)
      .then((m) => {
        if (cancelled) return;
        setNotesMap((prev) => {
          const next = new Map(prev);
          for (const [k, v] of m) {
            const key = billIdKey(String(k));
            if (!key) continue;
            const t = String(v ?? "").trim();
            if (!t) continue;
            const prevVal = (next.get(key) || "").trim();
            if (t.length > prevVal.length) next.set(key, t);
          }
          return next;
        });
      })
      .finally(() => {
        if (!cancelled) setNotesSyncPending(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, items]);

  const bankLines = bankMovementPagamentos ?? [];

  const centroOptions = useMemo(() => {
    const labels = new Set<string>();
    for (const item of items) {
      const lab = ccLabel(item, ccNameMap);
      if (lab && lab !== "—") labels.add(lab);
    }
    if (bankLines.length > 0) {
      labels.add("Mov. bancário (BC)");
    }
    return [...labels]
      .sort((a, b) => a.localeCompare(b, "pt-BR"))
      .map((label) => ({ value: label, label }));
  }, [items, ccNameMap, bankLines]);

  const setCentros = useCallback((centros: string[]) => {
    setFilters((prev) => ({ ...prev, centros }));
    setPage(0);
  }, []);

  const planoOptions = useMemo(() => {
    const labels = new Set<string>();
    for (const item of items) {
      const lab = accountLabel(item, paymentCatMap);
      if (lab && lab !== "—") labels.add(lab);
    }
    for (const line of bankLines) {
      const lab = (line.operationName || "").trim();
      if (lab && lab !== "—") labels.add(lab);
    }
    return [...labels]
      .sort((a, b) => a.localeCompare(b, "pt-BR"))
      .map((label) => ({ value: label, label }));
  }, [items, paymentCatMap, bankLines]);

  const setPlanos = useCallback((planos: string[]) => {
    setFilters((prev) => ({ ...prev, planos }));
    setPage(0);
  }, []);

  /* Filtragem — sem notesMap na dep para evitar loop; notas não filtram antes de carregar */
  const filteredOutcomes = useMemo(() => {
    return items.filter((item) => {
      const parStrFull = parcelaStr(item, mergedInstallmentsMap, fallbackTotalsByBill);
      const parUnresolved = billParcelaUnresolved(item.billId, mergedInstallmentsMap, maxInstallmentByBill);
      const parStrFilter = parUnresolved ? "" : parStrFull;
      const ccStr     = ccLabel(item, ccNameMap);
      const accStr    = accountLabel(item, paymentCatMap);
      const valorStr  = formatCurrency(outcomeParcelValorExibido(item));
      const statusStr = item.isPaid ? "pago" : "em aberto";
      const notesStr  = noteTextForItem(item, notesMap, bulkNotesByBillId);

      return (
        matchFilter(item.billId,       filters.titulo)    &&
        matchFilter(parStrFilter,      filters.parcela)   &&
        matchFilter(item.dueDate,      filters.vencimento)&&
        matchFilter(item.paymentDate,  filters.pagamento) &&
        matchFilter(item.creditorName, filters.credor)    &&
        matchFilter(valorStr,          filters.valor)     &&
        labelsFilterMatch(filters.centros, ccStr) &&
        labelsFilterMatch(filters.planos, accStr) &&
        matchFilter(notesStr,          filters.notas)     &&
        matchFilter(statusStr,         filters.status)
      );
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, filters, mergedInstallmentsMap, fallbackTotalsByBill, maxInstallmentByBill, ccNameMap, paymentCatMap, notesMap, bulkNotesByBillId]);

  const filteredBankLines = useMemo(
    () => bankLines.filter((line) => bankLineMatchesFilters(line, filters)),
    [bankLines, filters],
  );

  const filteredCombined = useMemo(() => {
    const rows: GridRow[] = [
      ...filteredOutcomes.map((item) => ({ kind: "outcome" as const, item })),
      ...filteredBankLines.map((line) => ({ kind: "bankMove" as const, line })),
    ];
    rows.sort((a, b) => {
      const da = gridRowSortDate(a);
      const db = gridRowSortDate(b);
      const c = da.localeCompare(db);
      if (c !== 0) return c;
      if (a.kind !== b.kind) return a.kind === "outcome" ? -1 : 1;
      if (a.kind === "outcome" && b.kind === "outcome") {
        return `${a.item.billId}-${a.item.installmentNumber}`.localeCompare(
          `${b.item.billId}-${b.item.installmentNumber}`,
        );
      }
      if (a.kind === "bankMove" && b.kind === "bankMove") {
        return a.line.id.localeCompare(b.line.id);
      }
      return 0;
    });
    return rows;
  }, [filteredOutcomes, filteredBankLines]);

  const totalRowCount = items.length + bankLines.length;
  const totalValue =
    items.reduce((s, i) => s + outcomeParcelValorExibido(i), 0) +
    bankLines.reduce((s, l) => s + l.amount, 0);

  const filteredValue = useMemo(
    () =>
      filteredCombined.reduce(
        (s, r) => s + (r.kind === "outcome" ? outcomeParcelValorExibido(r.item) : r.line.amount),
        0,
      ),
    [filteredCombined],
  );

  const pages    = Math.max(1, Math.ceil(filteredCombined.length / PER_PAGE));
  const safePage = Math.min(page, pages - 1);
  const visible  = filteredCombined.slice(safePage * PER_PAGE, (safePage + 1) * PER_PAGE);

  const activeFilters = hasActiveFilters(filters);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center flex-shrink-0">
            <FileText className="w-4 h-4 text-rose-600" />
          </div>
          <span className="text-sm font-semibold text-slate-800 leading-tight">{title}</span>
        </div>
      }
      size="full"
    >
      <div className="flex flex-col gap-4 h-full">

        {/* ── Linha de filtros ── */}
        <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-2 flex-shrink-0">
          <FilterInput placeholder="Nº Título"        value={filters.titulo}     onChange={set("titulo")}    />
          <FilterInput placeholder="Parcela"          value={filters.parcela}    onChange={set("parcela")}   />
          <FilterInput placeholder="Vencimento"       value={filters.vencimento} onChange={set("vencimento")}/>
          <FilterInput placeholder="Pagamento"        value={filters.pagamento}  onChange={set("pagamento")} />
          <FilterInput placeholder="Credor"           value={filters.credor}     onChange={set("credor")}    />
          <FilterInput placeholder="Valor"            value={filters.valor}      onChange={set("valor")}     />
          <div className="min-w-0 [&_button]:!h-[26px] [&_button]:!min-h-[26px] [&_button]:!py-0 [&_button]:!text-xs [&_button]:!rounded-md [&_button]:!bg-white [&_button]:!border-slate-200">
            <MultiSelect
              value={filters.centros}
              onChange={setCentros}
              options={centroOptions}
              placeholder="Centro de Custo"
              maxDisplay={1}
              dropdownMinWidth={300}
              stopPropagation
            />
          </div>
          <div className="min-w-0 [&_button]:!h-[26px] [&_button]:!min-h-[26px] [&_button]:!py-0 [&_button]:!text-xs [&_button]:!rounded-md [&_button]:!bg-white [&_button]:!border-slate-200">
            <MultiSelect
              value={filters.planos}
              onChange={setPlanos}
              options={planoOptions}
              placeholder="Plano Financeiro"
              maxDisplay={1}
              dropdownMinWidth={300}
              stopPropagation
            />
          </div>
          <FilterInput placeholder="Notas"            value={filters.notas}      onChange={set("notas")}     />
          <FilterInput placeholder="Status"           value={filters.status}     onChange={set("status")}    />
        </div>

        {(activeFilters || notesSyncPending || pdfError) && (
          <div className="flex-shrink-0 flex items-center gap-3 flex-wrap">
            {notesSyncPending && (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400">
                <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                Sincronizando observações com o Sienge (a tabela já mostra o que veio nas parcelas).
              </span>
            )}
            {pdfError && (
              <span className="text-[11px] text-rose-600">{pdfError}</span>
            )}
            {activeFilters && (
              <button
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors"
              >
                <X className="w-3 h-3" />
                Limpar filtros
              </button>
            )}
          </div>
        )}

        {statementDebits != null && statementDebits.length > 0 && (
          <div className="flex-shrink-0 rounded-xl border border-violet-100 bg-violet-50/50 p-4">
            <p className="text-xs font-semibold text-violet-900 uppercase tracking-wide mb-2">
              Débitos no extrato (contas SEMOG)
            </p>
            <StatementTable items={statementDebits} />
          </div>
        )}

        {/* ── Tabela ── */}
        <div className="flex-1 min-h-0 overflow-auto rounded-xl border border-slate-100">
          <table className="w-full text-sm border-collapse min-w-[900px]">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="border-b-2 border-slate-100">
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3 whitespace-nowrap">Nº Título</th>
                <th className="text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3 whitespace-nowrap">Parcela</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3 whitespace-nowrap">Vencimento</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3 whitespace-nowrap">Pagamento</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3">Credor</th>
                <th className="text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3 whitespace-nowrap">Valor</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3">Centro de Custo</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3">Plano Financeiro</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3">Notas</th>
                <th className="text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3 whitespace-nowrap">Pedido</th>
                <th className="text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center py-12 text-slate-400 text-sm">
                    Nenhum registro encontrado com os filtros aplicados.
                  </td>
                </tr>
              ) : (
                visible.map((row, idx) => {
                  if (row.kind === "bankMove") {
                    const line = row.line;
                    const payBr = line.movementDate ? formatDate(line.movementDate) : "—";
                    const cred = line.historico && line.historico !== "—" ? line.historico : line.operationName;
                    return (
                      <tr
                        key={`bm-${line.id}-${idx}`}
                        className="border-b border-slate-50 hover:bg-slate-50/70 transition-colors"
                      >
                        <td className="py-2.5 px-3 font-mono text-xs text-slate-500 whitespace-nowrap" title={line.id}>
                          —
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <span className="inline-flex items-center justify-center min-w-[44px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 text-xs font-semibold">
                            —
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-slate-400 whitespace-nowrap text-xs">—</td>
                        <td className="py-2.5 px-3 whitespace-nowrap text-xs">
                          <span className="text-emerald-700 font-medium">{payBr}</span>
                        </td>
                        <td className="py-2.5 px-3 text-slate-800 font-medium max-w-[160px] truncate text-xs" title={cred}>
                          {cred || "—"}
                        </td>
                        <td className="py-2.5 px-3 text-right font-semibold text-slate-900 whitespace-nowrap tabular-nums">
                          {formatCurrency(line.amount)}
                        </td>
                        <td className="py-2.5 px-3 text-slate-600 min-w-[120px] max-w-[200px] text-xs whitespace-normal break-words leading-snug">
                          Mov. bancário (BC)
                        </td>
                        <td className="py-2.5 px-3 text-slate-600 min-w-[140px] max-w-[260px] text-xs whitespace-normal break-words leading-snug">
                          {line.operationName || "—"}
                        </td>
                        <td className="py-2.5 px-3 text-slate-500 min-w-[160px] max-w-[280px] text-xs whitespace-normal break-words leading-snug">
                          {line.historico && line.historico !== "—" ? line.historico : "—"}
                        </td>
                        <td className="py-2.5 px-3 text-center text-slate-300 text-xs">—</td>
                        <td className="py-2.5 px-3 text-center">
                          <StatusBadge paid />
                        </td>
                      </tr>
                    );
                  }
                  const item = row.item;
                  const parStr = parcelaStr(item, mergedInstallmentsMap, fallbackTotalsByBill);
                  const parUnresolved = billParcelaUnresolved(item.billId, mergedInstallmentsMap, maxInstallmentByBill);
                  const ccStr  = ccLabel(item, ccNameMap);
                  const accStr = accountLabel(item, paymentCatMap);
                  const notesText = noteTextForItem(item, notesMap, bulkNotesByBillId);
                  const poRef = extractPurchaseOrderRefFromNotes(notesText);
                  const pdfRowKey = poRef
                    ? `${item.billId}-${item.installmentNumber}-${poRef}`
                    : null;
                  const pdfBusy = pdfRowKey != null && pdfLoadingKey === pdfRowKey;
                  return (
                    <tr
                      key={`${item.billId}-${item.installmentNumber}-${item.costCenterId}-${item.accountId}-${idx}`}
                      className="border-b border-slate-50 hover:bg-slate-50/70 transition-colors"
                    >
                      <td className="py-2.5 px-3 font-mono text-xs text-slate-600 whitespace-nowrap">
                        {item.billId || "—"}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <span
                          className={cn(
                            "inline-flex items-center justify-center min-w-[44px] min-h-[28px] px-2 py-0.5 rounded-md text-xs font-semibold",
                            parUnresolved
                              ? "bg-slate-50 text-slate-400"
                              : "bg-slate-100 text-slate-700 font-mono",
                          )}
                        >
                          {parUnresolved ? (
                            <Loader2 className="w-4 h-4 animate-spin text-slate-500" aria-label="Carregando parcela" />
                          ) : (
                            parStr
                          )}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-slate-700 whitespace-nowrap text-xs">
                        {item.dueDate ? formatDate(item.dueDate) : "—"}
                      </td>
                      <td className="py-2.5 px-3 whitespace-nowrap text-xs">
                        {item.paymentDate ? (
                          <span className="text-emerald-700 font-medium">{formatDate(item.paymentDate)}</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-slate-800 font-medium max-w-[160px] truncate text-xs">
                        {item.creditorName || "—"}
                      </td>
                      <td className="py-2.5 px-3 text-right font-semibold text-slate-900 whitespace-nowrap tabular-nums">
                        {formatCurrency(outcomeParcelValorExibido(item))}
                      </td>
                      <td className="py-2.5 px-3 text-slate-600 min-w-[120px] max-w-[200px] text-xs whitespace-normal break-words leading-snug" title={ccStr}>
                        {ccStr}
                      </td>
                      <td className="py-2.5 px-3 text-slate-600 min-w-[140px] max-w-[260px] text-xs whitespace-normal break-words leading-snug" title={accStr}>
                        {accStr}
                      </td>
                      <td className="py-2.5 px-3 text-slate-500 min-w-[160px] max-w-[280px] text-xs whitespace-normal break-words leading-snug">
                        {notesText || (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {poRef ? (
                          <button
                            type="button"
                            title={`Abrir PDF do pedido de compra #${poRef}`}
                            disabled={pdfBusy || pdfLoadingKey != null}
                            onClick={() => void handleGeneratePurchaseOrderPdf(item, notesText)}
                            className={cn(
                              "inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold transition-colors",
                              "bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-100",
                              "disabled:opacity-50 disabled:cursor-not-allowed",
                            )}
                          >
                            {pdfBusy ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <FileText className="w-3.5 h-3.5" />
                            )}
                            #{poRef}
                          </button>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <StatusBadge paid={item.isPaid} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── Paginação + Subtotal ── */}
        <div className="flex-shrink-0 flex flex-col gap-2">
          <SubtotalBar
            filtered={filteredCombined.length}
            total={totalRowCount}
            totalValue={totalValue}
            filteredValue={filteredValue}
          />

          {pages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">
                Página {safePage + 1} de {pages} · {filteredCombined.length} registro{filteredCombined.length !== 1 ? "s" : ""}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage(0)}
                  disabled={safePage === 0}
                  className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors"
                >
                  «
                </button>
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={safePage === 0}
                  className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
                  disabled={safePage >= pages - 1}
                  className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors"
                >
                  Próxima
                </button>
                <button
                  onClick={() => setPage(pages - 1)}
                  disabled={safePage >= pages - 1}
                  className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors"
                >
                  »
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </Modal>
  );
}
